// =============================================================================
// Optim TURPE V2 — L'ARBITRAGE « SANS / AVEC DÉPASSEMENTS » SERVI PAR LE MOTEUR
// (DEV-2167, M2 de la revue du 25/09/2026).
//
// Ce que le client doit vivre : à côté de chaque scénario, sa nette de période ;
// sous les deux, l'écart en euros ; au-dessus, le scénario conseillé et son
// motif. Le 25/09 sur le C4 interne, la page conseillait « sans CMDPS » par la
// seule forme du pic lue par l'écran, sans que le PDF, l'API ni le webhook ne
// sachent ce conseil. Le moteur le sert désormais dans `outputs.arbitrage`
// (courtage#251), avec les deux nettes et la phrase d'écart.
//
// Le bloc ne s'applique qu'à la paire qu'il décrit : ses deux identifiants
// doivent être ceux des deux études affichées. Sinon, ou sur une étude
// antérieure au lot, la page garde son repli : la règle v2 du 12/10/2026
// appliquée aux montants servis par les deux études, à défaut la forme du pic.
//
// La règle v2 du conseil (décision de Mathieu du 12/10/2026, DEV-2311 ; celle
// du moteur, `modules/optimturpe/arbitrage.py`). La première règle qui
// s'applique décide :
//  1. `couvrir-moins-cher` : les deux nettes sont servies et
//     nette(assumer) − nette(couvrir) ≤ 0 ⇒ couvrir, quelle que soit la durée
//     de dépassement : sans risque à prendre, ce sont les montants qui
//     décident (revue du lot D : une durée nulle faisait conseiller d'assumer
//     le scénario le moins rentable) ;
//  2. `aucun-depassement` : la structure la plus basse n'est dépassée à aucun
//     moment (durée servie pour toutes ses classes, somme nulle) et ses
//     dépassements estimés sont nuls ou non servis ⇒ assumer ;
//  3. `gain-couvre-le-risque` : gain > 0 et dépassements estimés servis,
//     lus sans arrondi, ≤ gain / 2 ⇒ assumer (au moins deux fois : même
//     triplés, les dépassements laisseraient assumer moins cher que couvrir) ;
//  4. `forme-du-pic` (DEV-783) sinon : pic isolé ⇒ assumer, autre forme ⇒
//     couvrir.
// =============================================================================

import {
    getDrivingDriverType,
    getPlagesDenivele,
    recommendScenarioFromShape,
} from "@modules/courtage/utils/optim-turpe-v2.helpers"
import type {
    OptimTurpeV2ArbitrageServi,
    OptimTurpeV2Etude,
} from "@modules/courtage/types/optim-turpe-v2"

export type ScenarioArbitrage = "sans" | "avec"

/** Les quatre règles du conseil v2 (12/10/2026), telles que le moteur les nomme, dans l'ordre. */
export type RegleDuConseil =
    | "couvrir-moins-cher"
    | "aucun-depassement"
    | "gain-couvre-le-risque"
    | "forme-du-pic"

const REGLES_DU_CONSEIL: readonly RegleDuConseil[] = [
    "couvrir-moins-cher",
    "aucun-depassement",
    "gain-couvre-le-risque",
    "forme-du-pic",
]

export type ArbitrageLu = {
    /** Le scénario conseillé par le moteur, ou `null` s'il ne le sert pas. */
    conseil: ScenarioArbitrage | null
    /** La règle v2 qui a décidé (`conseil.regle`), ou `null` si elle n'est pas servie. */
    regle: RegleDuConseil | null
    /** Le motif du conseil (`pic_isole`, `normal`, `saisonnier`…), tel que servi. */
    motif: string | null
    /** La classe motrice sur laquelle le moteur a lu la forme du pic. */
    plage: string | null
    /** Les montants de période servis pour chaque scénario (`null` = non servi). */
    montants: Record<ScenarioArbitrage, MontantsLus>
    /** La phrase d'écart composée par le moteur, ou `null`. */
    phrase: string | null
    /**
     * Le motif du conseil COMPOSÉ par le moteur (`outputs.arbitrage.motif`,
     * Courtage-6b, DEV-2217), ou `null` : une phrase, jamais un code.
     */
    motifServi: string | null
    /** Les dépassements assumés du scénario « avec » (sa CMDPS), servis par Courtage-6b. */
    depassementsAssumes: number | null
    /** La durée de dépassement du scénario « avec », en heures, ou `null`. */
    dureeDepassementH: number | null
}

export type MontantsLus = {
    nette: number | null
    ferme: number | null
    depassements: number | null
}

const SCENARIO_SERVI: Record<string, ScenarioArbitrage> = {
    sans_depassements: "sans",
    avec_depassements: "avec",
}

function montant(valeur: unknown): number | null {
    return typeof valeur === "number" && Number.isFinite(valeur)
        ? Math.round(valeur)
        : null
}

/**
 * Une dépense de dépassement servie, NON arrondie : l'arrondi à l'euro se fait
 * à l'écriture (`nombreEurosDepassement`), sinon 0,30 € deviendrait 0 et
 * s'écrirait « 0 € HT estimés » (un montant non nul ne s'écrit jamais 0).
 */
function montantBrut(valeur: unknown): number | null {
    return typeof valeur === "number" && Number.isFinite(valeur) ? valeur : null
}

function texte(valeur: unknown): string | null {
    return typeof valeur === "string" && valeur.trim().length > 0
        ? valeur
        : null
}

/** Une phrase servie : un code (`comportement_normal`) n'en est pas une. */
function phraseServie(valeur: unknown): string | null {
    const t = texte(valeur)
    return t !== null && /\s/.test(t.trim()) ? t : null
}

function heures(valeur: unknown): number | null {
    return typeof valeur === "number" && Number.isFinite(valeur) && valeur >= 0
        ? valeur
        : null
}

/**
 * La durée de dépassement servie : un nombre, ou un nombre par classe dont on
 * garde la plage motrice, sinon la somme des classes servies.
 */
function dureeServie(
    valeur: unknown,
    plage: string | null | undefined
): number | null {
    if (typeof valeur === "number") return heures(valeur)
    if (!valeur || typeof valeur !== "object") return null
    const parClasse = valeur as Record<string, unknown>
    if (plage && heures(parClasse[plage]) !== null) {
        return heures(parClasse[plage])
    }
    const servies = Object.values(parClasse)
        .map(heures)
        .filter((h): h is number => h !== null)
    return servies.length > 0 ? servies.reduce((a, b) => a + b, 0) : null
}

/**
 * Le bloc porte-t-il les clés de Courtage-6b ? Avant ce lot (v154),
 * `depassements_assumes_eur` lisait la VARIATION de dépassements, pas la CMDPS
 * de la jambe « avec » (DEV-2217) : la page ne le lit que sur un bloc neuf.
 */
function blocAvecFaitsDuScenario(bloc: OptimTurpeV2ArbitrageServi): boolean {
    return bloc.duree_depassement_h !== undefined || bloc.motif !== undefined
}

function regleServie(valeur: unknown): RegleDuConseil | null {
    return REGLES_DU_CONSEIL.find((r) => r === valeur) ?? null
}

function montantsLus(
    m: OptimTurpeV2ArbitrageServi["sans_depassements"]
): MontantsLus {
    return {
        nette: montant(m?.nette_eur),
        ferme: montant(m?.ferme_eur),
        depassements: montant(m?.depassements_eur),
    }
}

/**
 * L'arbitrage servi pour CETTE paire, ou `null` : étude antérieure au lot,
 * scénario seul, ou bloc qui décrit une autre paire (relance, étude supprimée).
 */
export function lireArbitrageServi(
    sans: OptimTurpeV2Etude | null,
    avec: OptimTurpeV2Etude | null
): ArbitrageLu | null {
    if (!sans?._id || !avec?._id) return null
    const bloc: OptimTurpeV2ArbitrageServi | null | undefined =
        sans.arbitrage ?? avec.arbitrage
    if (!bloc || typeof bloc !== "object") return null
    if (
        bloc.sans_depassements?._id !== sans._id ||
        bloc.avec_depassements?._id !== avec._id
    ) {
        return null
    }
    return {
        conseil: SCENARIO_SERVI[String(bloc.conseil?.scenario)] ?? null,
        regle: regleServie(bloc.conseil?.regle),
        motif: texte(bloc.conseil?.motif),
        plage: texte(bloc.conseil?.plage_motrice),
        montants: {
            sans: montantsLus(bloc.sans_depassements),
            avec: montantsLus(bloc.avec_depassements),
        },
        phrase: texte(bloc.phrase),
        motifServi: phraseServie(bloc.motif),
        depassementsAssumes: blocAvecFaitsDuScenario(bloc)
            ? montantBrut(bloc.depassements_assumes_eur)
            : null,
        dureeDepassementH: dureeServie(
            bloc.duree_depassement_h,
            bloc.conseil?.plage_motrice
        ),
    }
}

/**
 * La durée de dépassement du scénario « Assumer les dépassements », lue sur le
 * bloc de la paire porté par UNE étude (la preuve n'en reçoit qu'une). `null`
 * sur l'étude « avec » elle-même, qui dit son propre dépassement, et sur un
 * bloc qui ne la sert pas.
 */
export function lireDureeDepassementDeLaPaire(
    etude: OptimTurpeV2Etude | null | undefined,
    plage: string | null | undefined
): number | null {
    const bloc = etude?.arbitrage
    if (!bloc || typeof bloc !== "object" || !etude?._id) return null
    if (bloc.avec_depassements?._id === etude._id) return null
    if (bloc.sans_depassements?._id !== etude._id) return null
    return dureeServie(
        bloc.duree_depassement_h,
        plage ?? bloc.conseil?.plage_motrice
    )
}

/** Un nombre fini tel que servi, sans arrondi, ou `null` : jamais un 0. */
function fini(valeur: unknown): number | null {
    return typeof valeur === "number" && Number.isFinite(valeur) ? valeur : null
}

/**
 * Les dépassements estimés du scénario « Assumer les dépassements », tels que
 * servis, SANS arrondi (revue du lot E : 50,4 € arrondis à 50 passaient sous
 * la moitié d'un gain de 100 €). Mêmes sources que `getCmdpsEstimeeEur`, qui
 * arrondit pour l'affichage. `null` s'ils ne sont pas servis : jamais un 0.
 */
function depassementsEstimesBruts(avec: OptimTurpeV2Etude): number | null {
    return (
        fini(avec.economie?.cmdps_estimee_eur) ??
        fini(avec.meilleure?.couts?.turpeCMDPS)
    )
}

/** Le scénario dans le sens duquel chaque règle conseille. */
export const SCENARIO_DE_LA_REGLE: Readonly<
    Record<Exclude<RegleDuConseil, "forme-du-pic">, ScenarioArbitrage>
> = {
    "couvrir-moins-cher": "sans",
    "aucun-depassement": "avec",
    "gain-couvre-le-risque": "avec",
}

/** Le conseil recomposé par l'écran, avec la règle qui l'a décidé et ses montants. */
export type ConseilDeRepli = {
    scenario: ScenarioArbitrage
    regle: RegleDuConseil
    /** Le gain d'assumer face à couvrir (nette « avec » − nette « sans »), ou `null`. */
    gainEur: number | null
    /** Les dépassements estimés du scénario « Assumer les dépassements », ou `null`. */
    depassementsEur: number | null
}

/**
 * Règle 1 en repli : la durée de dépassement de la structure la plus basse,
 * servie dans le bloc de la paire POUR CHACUNE de ses classes, est nulle.
 * Une classe sans durée rend la règle inapplicable : l'absence n'est pas un 0.
 */
function aucunDepassementServi(
    bloc: OptimTurpeV2ArbitrageServi | null | undefined,
    avec: OptimTurpeV2Etude
): boolean {
    const duree = bloc?.duree_depassement_h
    if (!duree || typeof duree !== "object") return false
    const classes = getPlagesDenivele(avec)
    if (classes.length === 0) return false
    const valeurs = classes.map((c) => heures(duree[c]))
    if (valeurs.some((h) => h === null)) return false
    return (valeurs as number[]).reduce((a, b) => a + b, 0) === 0
}

/**
 * Q25 : la carte « Assumer les dépassements » dit « Aucun dépassement sur la
 * période étudiée » seulement sur un FAIT servi : la règle du conseil
 * `aucun-depassement`, ou une durée de dépassement servie égale à zéro.
 * Jamais sur l'arrondi d'un montant en euros (un dépassement facturé moins
 * de 0,50 € reste un dépassement).
 */
export function aucunDepassementEtabli(
    regle: RegleDuConseil | null | undefined,
    dureeDepassementH: number | null | undefined
): boolean {
    return regle === "aucun-depassement" || dureeDepassementH === 0
}

/**
 * Le conseil de l'écran quand le moteur n'en sert pas (étude antérieure au
 * lot, ou bloc d'une autre paire) : la règle v2 du 12/10/2026, la même que
 * celle du moteur, appliquée aux montants servis par les deux études ; à
 * défaut de montants, la forme du pic lue par l'écran (DEV-783).
 */
export function conseilDeRepli(
    sans: OptimTurpeV2Etude | null,
    avec: OptimTurpeV2Etude | null
): ConseilDeRepli {
    const reference = avec ?? sans
    const forme: ScenarioArbitrage = reference
        ? recommendScenarioFromShape(reference)
        : "avec"
    if (!sans || !avec) {
        return {
            scenario: forme,
            regle: "forme-du-pic",
            gainEur: null,
            depassementsEur: null,
        }
    }
    // La durée ne se lit que sur le bloc de CETTE paire.
    const bloc = sans.arbitrage ?? avec.arbitrage
    const blocDeLaPaire =
        !!sans._id &&
        bloc?.sans_depassements?._id === sans._id &&
        bloc?.avec_depassements?._id === avec._id
            ? bloc
            : null
    const depassementsEur = depassementsEstimesBruts(avec)
    const netteSans = fini(sans.economie?.nette_eur)
    const netteAvec = fini(avec.economie?.nette_eur)
    const gainEur =
        netteSans !== null && netteAvec !== null ? netteAvec - netteSans : null
    // 1. Assumer ne fait rien gagner : couvrir, même sans aucun dépassement.
    if (gainEur !== null && gainEur <= 0) {
        return {
            scenario: "sans",
            regle: "couvrir-moins-cher",
            gainEur,
            depassementsEur,
        }
    }
    // 2. Jamais dépassée, et aucun dépassement chiffré qui le contredirait.
    if (
        (depassementsEur === null || depassementsEur === 0) &&
        aucunDepassementServi(blocDeLaPaire, avec)
    ) {
        return {
            scenario: "avec",
            regle: "aucun-depassement",
            gainEur,
            depassementsEur,
        }
    }
    // 3. Le gain fait au moins deux fois les dépassements estimés.
    if (
        gainEur !== null &&
        depassementsEur !== null &&
        depassementsEur >= 0 &&
        depassementsEur <= gainEur / 2
    ) {
        return {
            scenario: "avec",
            regle: "gain-couvre-le-risque",
            gainEur,
            depassementsEur,
        }
    }
    return { scenario: forme, regle: "forme-du-pic", gainEur, depassementsEur }
}

/**
 * Le scénario conseillé quand il est ÉTABLI : servi par le moteur, ou tiré
 * par le repli d'une règle de montants (1 à 3), ou de la forme du pic quand
 * elle est lue. `null` quand rien ne le fonde (aucun conseil servi, montants
 * qui ne tranchent pas, forme du pic inconnue) : `scenarioConseille` retombe
 * alors sur « Couvrir les dépassements » par défaut, ce qu'une ligne qui ne
 * nomme pas son scénario ne doit pas présenter comme un conseil (revue du
 * lot 3).
 */
export function conseilEtabli(
    sans: OptimTurpeV2Etude | null,
    avec: OptimTurpeV2Etude | null
): ScenarioArbitrage | null {
    const servi = lireArbitrageServi(sans, avec)?.conseil
    if (servi) return servi
    const repli = conseilDeRepli(sans, avec)
    if (repli.regle !== "forme-du-pic") return repli.scenario
    const reference = avec ?? sans
    return reference && getDrivingDriverType(reference) !== undefined
        ? repli.scenario
        : null
}

/**
 * Le scénario conseillé : celui que le moteur sert, sinon, en repli pour une
 * étude antérieure, la règle v2 du 12/10/2026 recomposée par l'écran sur les
 * montants servis, à défaut la forme du pic (`conseilDeRepli`).
 */
export function scenarioConseille(
    sans: OptimTurpeV2Etude | null,
    avec: OptimTurpeV2Etude | null
): ScenarioArbitrage {
    const servi = lireArbitrageServi(sans, avec)?.conseil
    if (servi) return servi
    return conseilDeRepli(sans, avec).scenario
}

/** Le code du moteur pour chaque scénario, tel que la route scénario l'attend. */
export const CODE_SCENARIO_RETENU: Record<
    ScenarioArbitrage,
    "sans_depassements" | "avec_depassements"
> = {
    sans: "sans_depassements",
    avec: "avec_depassements",
}

/**
 * Le scénario RETENU par l'utilisateur et enregistré avec la paire (lot 4g,
 * C25, D17 (a)) : `scenario_retenu`, servi à la racine de chaque étude par la
 * lecture de la page (`champs_page`). Le moteur l'écrit sur les deux études de
 * la paire ; deux valeurs servies qui se contredisent ne fondent rien
 * (`null`), une valeur servie sur une seule jambe suffit. `null` tant que rien
 * n'est enregistré, ou sur une étude antérieure au lot, qui ne le sert pas :
 * la page affiche alors le conseil, et l'historique le scénario du montant.
 */
export function scenarioRetenuServi(
    sans: OptimTurpeV2Etude | null,
    avec: OptimTurpeV2Etude | null
): ScenarioArbitrage | null {
    const lus = [sans, avec]
        .map((e) => SCENARIO_SERVI[String(e?.scenario_retenu)] ?? null)
        .filter((s): s is ScenarioArbitrage => s !== null)
    if (lus.length === 0) return null
    return lus.every((s) => s === lus[0]) ? lus[0] : null
}
